import { Controller } from "@hotwired/stimulus"

// One-time "what's new" tour for the new top bar. Anchors to the shared
// controls by their stable title= attributes; both the global top nav and the
// story-channels header render these, so it works on either landing page.
// ponytail: desktop-first — element steps are dropped when their anchor isn't
// visible (mobile bars are hidden md:*), leaving the centered intro + final tip.
const STEPS = [
    { center: true,
      title: "Say hello to the new top bar",
      body: "We moved navigation to the top. Here's a 20-second tour of what changed." },
    { selector: '[title="Apps"]',
      title: "Apps launcher",
      body: "Your gateway to every module — reports, data sources and settings. Click the logo anytime." },
    { selector: '[data-tour="search"]',
      title: "Search anything",
      body: "Jump to any report, page or setting instantly. Works from anywhere with Ctrl+K." },
    { selector: '[title="Workspace"]',
      title: "Workspace & account",
      body: "Switch organization and manage your profile from the right-hand menu." },
    { selector: '[data-tour="search"]', instruction: true,
      title: "Try it now",
      body: "Click Search, type “account groupings” and press Enter to jump straight there." },
]

export default class extends Controller {
    static targets = ["backdrop", "ring", "card", "counter", "title", "body", "back", "next", "try"]
    static values = { url: String }

    connect() {
        this.steps = STEPS.filter(s => s.center || s.instruction || this.visible(s.selector))
        this.index = 0
        this.reposition = () => this.place(this.currentAnchor())
        window.addEventListener("resize", this.reposition)
        window.addEventListener("scroll", this.reposition, true)
        this.render()
    }

    disconnect() {
        window.removeEventListener("resize", this.reposition)
        window.removeEventListener("scroll", this.reposition, true)
    }

    next() {
        if (this.index >= this.steps.length - 1) return this.finish()
        this.index++
        this.render()
    }

    back() {
        if (this.index === 0) return
        this.index--
        this.render()
    }

    skip() { this.finish() }

    finish() {
        this.markSeen()
        this.element.remove()
    }

    // Culmination step: end the tour, then open the real search palette so the
    // user can immediately type their query (order matters — remove the backdrop
    // before the palette, which sits at a lower z-index, opens).
    trySearch() {
        const btn = this.visible('[data-tour="search"]')
        this.markSeen()
        this.element.remove()
        if (btn) btn.click()
    }

    render() {
        const step = this.steps[this.index]
        this.counterTarget.textContent = `Step ${this.index + 1} of ${this.steps.length}`
        this.titleTarget.textContent = step.title
        this.bodyTarget.textContent = step.body
        this.backTarget.classList.toggle("hidden", this.index === 0)
        this.tryTarget.classList.toggle("hidden", !step.instruction)
        this.nextTarget.textContent = this.index === this.steps.length - 1 ? "Done" : "Next"
        this.place(this.currentAnchor())
    }

    currentAnchor() {
        const step = this.steps[this.index]
        return step && step.selector ? this.visible(step.selector) : null
    }

    place(anchor) {
        const card = this.cardTarget
        card.classList.remove("hidden")
        if (!anchor) {
            this.ringTarget.classList.add("hidden")
            card.style.top = "50%"
            card.style.left = "50%"
            card.style.transform = "translate(-50%, -50%)"
            return
        }
        card.style.transform = ""
        const r = anchor.getBoundingClientRect()
        const ring = this.ringTarget
        ring.classList.remove("hidden")
        ring.style.top = `${r.top - 4}px`
        ring.style.left = `${r.left - 4}px`
        ring.style.width = `${r.width + 8}px`
        ring.style.height = `${r.height + 8}px`

        const cw = card.offsetWidth, ch = card.offsetHeight
        const vw = window.innerWidth, vh = window.innerHeight
        let top = r.bottom + 12
        if (top + ch > vh - 8) top = Math.max(8, r.top - ch - 12)
        let left = Math.min(Math.max(8, r.left + r.width / 2 - cw / 2), vw - cw - 8)
        card.style.top = `${top}px`
        card.style.left = `${left}px`
    }

    visible(selector) {
        return [...document.querySelectorAll(selector)].find(el => {
            const r = el.getBoundingClientRect()
            return r.width > 0 && r.height > 0 && el.offsetParent !== null
        })
    }

    markSeen() {
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        fetch(this.urlValue, {
            method: "PATCH",
            headers: { "X-CSRF-Token": token, "Accept": "application/json" },
        }).catch(() => {})
    }
}
